import { createContext, useContext, useState, type ReactNode, useCallback } from 'react';
import { Toast, type ToastProps } from './Toast';

interface ToastContextValue {
  showToast: (
    message: string,
    options?: Partial<Omit<ToastProps, 'id' | 'message' | 'onClose'>>
  ) => void;
  hideToast: (id: string) => void;
  clearAll: () => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
}

interface ToastProviderProps {
  children: ReactNode;
}

interface ToastData extends Omit<ToastProps, 'onClose'> {
  id: string;
}

/**
 * ToastProvider Component
 *
 * Provides toast notification context and manages toast lifecycle.
 * Wrap your app with this provider to enable toast notifications globally.
 *
 * Usage:
 * ```tsx
 * const { showToast } = useToast();
 * showToast('Task claimed successfully!', { variant: 'success' });
 * showToast('Failed to claim task', { variant: 'error', showRetry: true, onRetry: handleRetry });
 * ```
 */
export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastData[]>([]);

  const showToast = useCallback(
    (message: string, options?: Partial<Omit<ToastProps, 'id' | 'message' | 'onClose'>>) => {
      const id = `toast-${Date.now()}-${Math.random()}`;
      const newToast: ToastData = {
        id,
        message,
        variant: options?.variant ?? 'info',
        duration: options?.duration ?? 5000,
        showRetry: options?.showRetry ?? false,
        ...(options?.onRetry && { onRetry: options.onRetry }),
      };
      setToasts((prev) => [...prev, newToast]);
    },
    []
  );

  const hideToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const clearAll = useCallback(() => {
    setToasts([]);
  }, []);

  return (
    <ToastContext.Provider value={{ showToast, hideToast, clearAll }}>
      {children}
      <div
        className="fixed top-4 right-4 z-50 flex flex-col gap-2 pointer-events-none"
        aria-label="Notifications"
      >
        {toasts.map((toast) => (
          <div key={toast.id} className="pointer-events-auto">
            <Toast {...toast} onClose={hideToast} />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
